/**
 * Movement History Page
 * Displays inventory movement history with filters
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { inventoryApi } from '../lib/api/inventory';
import type { InventoryMovement, Ingredient } from '../types/inventory';
import { MovementType, MOVEMENT_TYPE_LABELS, MOVEMENT_TYPE_COLORS } from '../types/inventory';

export default function MovementHistoryPage() {
  const [loading, setLoading] = useState(true);
  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [ingredients, setIngredients] = useState<Map<string, Ingredient>>(new Map());
  const [filteredMovements, setFilteredMovements] = useState<InventoryMovement[]>([]);
  const [days, setDays] = useState(30);
  const [typeFilter, setTypeFilter] = useState<MovementType | 'ALL'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, [days]);

  useEffect(() => {
    applyFilters();
  }, [movements, typeFilter, searchQuery]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [movementsData, ingredientsData] = await Promise.all([
        inventoryApi.getMovementHistory(days),
        inventoryApi.getIngredients(),
      ]);

      setMovements(movementsData);

      // Create ingredient map for quick lookup
      const ingredientMap = new Map<string, Ingredient>();
      ingredientsData.forEach((ing) => ingredientMap.set(ing.id, ing));
      setIngredients(ingredientMap);
    } catch (error) {
      console.error('Failed to load movement history:', error);
      alert('Errore nel caricamento dello storico movimenti');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...movements];

    // Type filter
    if (typeFilter !== 'ALL') {
      filtered = filtered.filter((mov) => mov.movementType === typeFilter);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((mov) => {
        const ingredient = ingredients.get(mov.ingredientId);
        return (
          ingredient?.name.toLowerCase().includes(query) ||
          mov.notes?.toLowerCase().includes(query) ||
          mov.performedBy?.toLowerCase().includes(query)
        );
      });
    }

    // Sort by date (newest first)
    filtered.sort((a, b) => new Date(b.movementDate).getTime() - new Date(a.movementDate).getTime());

    setFilteredMovements(filtered);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getQuantityDisplay = (movement: InventoryMovement) => {
    const ingredient = ingredients.get(movement.ingredientId);
    const sign = movement.movementType === MovementType.PURCHASE ? '+' : '-';
    const color =
      movement.movementType === MovementType.PURCHASE ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';

    return (
      <span className={`font-semibold ${color}`}>
        {sign}
        {movement.quantity} {ingredient?.unitOfMeasure || ''}
      </span>
    );
  };

  const getTotalCost = () => {
    return filteredMovements
      .filter((mov) => mov.totalCost)
      .reduce((sum, mov) => sum + (mov.totalCost || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-900 dark:to-slate-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-slate-400">Caricamento Storico...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-900 dark:to-slate-950 transition-colors duration-300">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl border-b border-gray-200 dark:border-slate-700/50 shadow-sm dark:shadow-xl dark:shadow-slate-950/50">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white dark:drop-shadow-[0_2px_10px_rgba(255,255,255,0.15)]">📋 Storico Movimenti</h1>
              <p className="text-gray-600 dark:text-slate-400 mt-1">Monitora tutti i movimenti di magazzino</p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/dashboard/inventory/purchase"
                className="px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-600 shadow-lg dark:shadow-green-900/50 transition-all duration-200 flex items-center gap-2"
              >
                <span>📥</span>
                Registra Acquisto
              </Link>
              <Link
                to="/dashboard/inventory/ingredients"
                className="px-4 py-2 text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
              >
                Ingredienti
              </Link>
            </div>
          </div>

          {/* Filters */}
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca per ingrediente, note o utente..."
                className="w-full px-4 py-3 pl-12 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent"
              />
              <svg
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            {/* Filter Buttons */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Period Filter */}
              <select
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value))}
                className="px-4 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent"
              >
                <option value={7}>Ultimi 7 giorni</option>
                <option value={30}>Ultimi 30 giorni</option>
                <option value={90}>Ultimi 90 giorni</option>
                <option value={365}>Ultimo anno</option>
              </select>

              {/* Type Filter */}
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as MovementType | 'ALL')}
                className="px-4 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent"
              >
                <option value="ALL">Tutti i tipi</option>
                {Object.entries(MOVEMENT_TYPE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>

              {/* Results Count */}
              <div className="ml-auto text-sm text-gray-600 dark:text-slate-400">
                {filteredMovements.length} movimenti
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl rounded-lg shadow-sm dark:shadow-xl dark:shadow-slate-950/50 border border-gray-200 dark:border-slate-700/50 dark:ring-1 dark:ring-white/5 p-4 transition-all duration-300">
            <div className="text-sm text-gray-600 dark:text-slate-400 mb-1">Totale Movimenti</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{filteredMovements.length}</div>
          </div>
          <div className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl rounded-lg shadow-sm dark:shadow-xl dark:shadow-slate-950/50 border border-gray-200 dark:border-slate-700/50 dark:ring-1 dark:ring-white/5 p-4 transition-all duration-300">
            <div className="text-sm text-gray-600 dark:text-slate-400 mb-1">Acquisti</div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {filteredMovements.filter((m) => m.movementType === MovementType.PURCHASE).length}
            </div>
          </div>
          <div className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl rounded-lg shadow-sm dark:shadow-xl dark:shadow-slate-950/50 border border-gray-200 dark:border-slate-700/50 dark:ring-1 dark:ring-white/5 p-4 transition-all duration-300">
            <div className="text-sm text-gray-600 dark:text-slate-400 mb-1">Sprechi</div>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {filteredMovements.filter((m) => m.movementType === MovementType.WASTE).length}
            </div>
          </div>
          <div className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl rounded-lg shadow-sm dark:shadow-xl dark:shadow-slate-950/50 border border-gray-200 dark:border-slate-700/50 dark:ring-1 dark:ring-white/5 p-4 transition-all duration-300">
            <div className="text-sm text-gray-600 dark:text-slate-400 mb-1">Costo Totale</div>
            <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">€{getTotalCost().toFixed(2)}</div>
          </div>
        </div>

        {/* Movements Table */}
        {filteredMovements.length === 0 ? (
          <div className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl rounded-xl shadow-sm dark:shadow-xl dark:shadow-slate-950/50 border border-gray-200 dark:border-slate-700/50 dark:ring-1 dark:ring-white/5 p-12 text-center transition-all duration-300">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Nessun movimento trovato
            </h3>
            <p className="text-gray-600 dark:text-slate-400">Prova a modificare i filtri o il periodo</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl rounded-xl shadow-sm dark:shadow-xl dark:shadow-slate-950/50 border border-gray-200 dark:border-slate-700/50 dark:ring-1 dark:ring-white/5 overflow-hidden transition-all duration-300"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                <thead className="bg-gray-50 dark:bg-slate-800/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                      Ingrediente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                      Quantità
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                      Costo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                      Note
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-800/30 divide-y divide-gray-200 dark:divide-slate-700">
                  {filteredMovements.map((movement, index) => {
                    const ingredient = ingredients.get(movement.ingredientId);
                    return (
                      <motion.tr
                        key={movement.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatDate(movement.movementDate)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              MOVEMENT_TYPE_COLORS[movement.movementType]
                            }`}
                          >
                            {MOVEMENT_TYPE_LABELS[movement.movementType]}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {ingredient?.name || 'N/A'}
                          </div>
                          {ingredient?.description && (
                            <div className="text-xs text-gray-500 dark:text-slate-400">{ingredient.description}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {getQuantityDisplay(movement)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {movement.totalCost ? `€${movement.totalCost.toFixed(2)}` : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-slate-400 max-w-xs truncate">
                          {movement.notes || '-'}
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
